import { DocumentNode, TypedDocumentNode, ApolloClient, OperationVariables } from "@apollo/client";
import { useMutation } from "@apollo/client/react";
import { useCallback } from "react";

import FlashContext from "components/FlashMessages/FlashContext";
import { ApolloMutationHook, MutationHookCallback, MutationHandlerOptions } from "types/Mutation";
import { handleMutation } from "utils/mutations";

import useTypedContext from "./useTypedContext";

// * types are partially duplicated from apollo library
type UseMutationHandlerProps<TData, TVariables extends OperationVariables> = {
  gql: DocumentNode | TypedDocumentNode<TData, TVariables>;
  options?: MutationHandlerOptions<TData, TVariables>;
};

const useMutationHandler = <FetchData, MutationVariables extends OperationVariables>({
  gql,
  options,
}: UseMutationHandlerProps<FetchData, MutationVariables>): [
  (
    variables: MutationVariables,
    mutationCallback?: MutationHookCallback<FetchData>
  ) => Promise<void | ApolloClient.MutateResult<FetchData>>,
  useMutation.Result<FetchData>,
] => {
  const { onError } = useTypedContext(FlashContext);
  const errorCallback = options?.onError || onError;

  const [mutation, mutationResult] = useMutation<FetchData, MutationVariables>(gql, {
    ...options,
    ...(!options?.propagateError && { onError: errorCallback }),
    ...(options?.suppressFlashError && {
      context: { ...(options?.context || {}), suppressFlashError: true },
    }),
  });

  // Apollo's mutate function type is computed via a mapped type (MakeRequiredVariablesOptional)
  // that TS can't prove is structurally identical to the bare MutationVariables generic at this
  // unresolved-generic call site, even though it always is here: we never pre-configure variables
  // in the hook-level options above, so no variable becomes optional.
  const typedMutation = mutation as ApolloMutationHook<FetchData, MutationVariables>;

  const handleMutationCallback = useCallback(
    (variables: MutationVariables, mutationCallback?: MutationHookCallback<FetchData>) => {
      return handleMutation({
        mutation: typedMutation,
        variables,
        mutationCallback,
        propagateError: options?.propagateError,
        errorCallback,
      });
    },
    [typedMutation, errorCallback, options?.propagateError]
  );

  return [handleMutationCallback, mutationResult];
};

/**
 * Create a mutation hook for use mostly on View Pages, Details, etc
 * * It UPDATES Apollo cache with the mutation result by default
 */
export const createDefaultMutationHook =
  <TData, TVariables extends OperationVariables>(gql: TypedDocumentNode<TData, TVariables>) =>
  (options: MutationHandlerOptions<TData, TVariables> = {}) => {
    return useMutationHandler({
      gql,
      options: {
        propagateError: false,
        awaitRefetchQueries: true,
        ...options,
      },
    });
  };

/**
 * Create a mutation hook for use in Bulk Actions
 * * It DOESN'T UPDATE Apollo cache with the mutation result
 */
export const createBulkMutationHook =
  <TData, TVariables extends OperationVariables>(gql: TypedDocumentNode<TData, TVariables>) =>
  (options: MutationHandlerOptions<TData, TVariables> = {}) => {
    return useMutationHandler({
      gql,
      options: {
        propagateError: true,
        fetchPolicy: "no-cache",
        ...options,
      },
    });
  };
